/* eslint-disable */
import {
  matchesStatusSet, matchesSunOptic, matchesValueSet, matchesFactorySet,
  matchesSeasonSet, matchesSelectionSets, devStageBucket, devHasNoImage, devIsImported,
} from './devStatusFilter';
import { LOCK_KEY } from './devLock';

// ============================================================
// devFacetCounts — faceted counts for the Developments filter bar (CLB-079).
// "Faceted" = the number beside an option is what the board would show if that
// option were the ONLY choice on its own axis, with every OTHER filter (search,
// the other axes, the stage/flag choice) kept. A pile of choices therefore never
// zeroes its own axis's list — you can still see and add siblings.
//
// Convention #61: the predicates are the SAME shared functions useDevFilters
// composes (devStatusFilter.js) — nothing is re-derived here, so a count can
// never disagree with the list it promises. One pass computes, per dev, which
// axes it FAILS; an axis's counts then run only over devs that fail nothing or
// fail just that axis. No query, no RPC (Convention #57/#61: client-side over
// the in-memory list the board already holds).
// ============================================================

const EMPTY = new Set();
export const FACET_AXES = ['designer', 'factory', 'material', 'shape', 'season', 'sunOptic', 'decision', 'procurement', 'type'];
export const STAGE_KEYS = ['Drawing stage', 'Hand made sample stage', 'Color sample stage', '_done', LOCK_KEY, '_stopped'];
const STAGE_ONLY = new Set(STAGE_KEYS);

// predicate per axis: (dev, Set) → boolean. Empty Set = pass-through (as in the shared predicates).
const PRED = {
  designer: (d, s) => matchesValueSet(s, d.designer || d.data?.designer),
  type: (d, s) => matchesValueSet(s, d.dev_type || d.data?.dev_type),
  sunOptic: (d, s) => matchesSunOptic(d, s),
  material: (d, s) => matchesValueSet(s, d.material || d.data?.material),
  shape: (d, s) => matchesValueSet(s, d.shape || d.data?.shape),
  season: (d, s) => matchesSeasonSet(s, d.season || d.data?.season),
  factory: (d, s) => matchesFactorySet(s, d.fty || d.data?.fty),
  decision: (d, s) => matchesSelectionSets(d, s, EMPTY),
  procurement: (d, s) => matchesSelectionSets(d, EMPTY, s),
};

function splitStatus(status) {
  const stage = new Set(), flags = new Set();
  for (const k of status || []) (STAGE_ONLY.has(k) ? stage : flags).add(k);
  return { stage, flags };
}

/**
 * @param {object} p
 * @param {Array} p.devs        the in-memory board list (archived already out)
 * @param {string} p.search     the DEBOUNCED search string (lower-cased inside)
 * @param {object} p.filters    uiStore.devFilters (a Set per axis)
 * @param {object} p.options    { axis: [{ value }] } the option vocabularies
 * @param {(d,q)=>boolean} [p.matchesSearch]  the board's own search predicate (hook injects devMatchesSearch)
 * @param {(d)=>boolean} [p.inScope]  designer scoping the board applies to non-admins
 * @returns {{ total:number, axes:Record<string,Record<string,number>>, stage:Record<string,number> }}
 */
export function computeFacets({ devs, search, filters, options, inScope, matchesSearch }) {
  const q = (search || '').toLowerCase();
  const f = filters || {};
  const { stage: stageSel, flags: flagSel } = splitStatus(f.status);
  const bit = {}; FACET_AXES.forEach((a, i) => { bit[a] = 1 << i; });
  const BIT_STAGE = 1 << FACET_AXES.length, BIT_FLAG = BIT_STAGE << 1;

  const rows = [];
  for (const d of devs || []) {
    if (inScope && !inScope(d)) continue;
    if (q && matchesSearch && !matchesSearch(d, q)) continue;
    let m = 0;
    for (const a of FACET_AXES) if (f[a] && f[a].size && !PRED[a](d, f[a])) m |= bit[a];
    if (stageSel.size && !matchesStatusSet(d, stageSel)) m |= BIT_STAGE;
    if (flagSel.size && !matchesStatusSet(d, flagSel)) m |= BIT_FLAG;
    rows.push([d, m]);
  }

  let total = 0;
  for (const [, m] of rows) if (m === 0) total++;

  const axes = {};
  for (const a of FACET_AXES) {
    const out = {}; axes[a] = out;
    const vals = (options && options[a]) || [];
    if (!vals.length) continue;
    const cand = rows.filter(([, m]) => (m & ~bit[a]) === 0).map((r) => r[0]);
    for (const o of vals) { const s = new Set([o.value]); let n = 0; for (const d of cand) if (PRED[a](d, s)) n++; out[o.value] = n; }
  }

  // Stage pills strip their own group (the stage choice) but keep the flag choice; flags the reverse.
  const stage = {}; STAGE_KEYS.forEach((k) => { stage[k] = 0; }); stage._no_image = 0; stage._imported = 0;
  for (const [d, m] of rows) {
    if ((m & ~BIT_STAGE) === 0) { const b = devStageBucket(d); if (b) stage[b]++; }
    if ((m & ~BIT_FLAG) === 0) { if (devHasNoImage(d)) stage._no_image++; if (devIsImported(d)) stage._imported++; }
  }
  return { total, axes, stage };
}
